import {Box, Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {ModalityCodeEnum, RefundStatusEnum} from 'domain/usecases';
import {StyledCard, TagChip} from 'presentation/components';
import {FeaturesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {textHandle} from 'presentation/utils/textHandle';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {cardStyleByStatus} from 'presentation/pages/private/refund/constants/cardByStatus';
import {formatRefundAmount, formatRefundDate} from 'presentation/pages/private/refund/utils';
import {RefundCardProps} from 'presentation/pages/private/refund/components/RefundCard/types';

const {modalityLabel, protocol} = translations['pt-br'].commons;
const {
	cardActionSeeSummary,
	detailValueNotInformed,
	expectedPaymentDateTitle,
	paymentDateTitle,
	providerLabel,
	providerNamePlaceholder,
	amountToReceive,
	requestedAt,
	modality: refundModalityLabels,
	status: refundStatusLabels,
} = translations['pt-br'].privateFlow.consultRefund;
const {cards: modalityCards} = translations['pt-br'].privateFlow.refund.serviceType;

const MODALITY_LABEL_BY_CODE: Record<ModalityCodeEnum, string> = {
	[ModalityCodeEnum.MEDICAL_APPOINTMENT]: modalityCards.consult,
	[ModalityCodeEnum.SIMPLE_EXAMS]: refundModalityLabels.simpleExam,
	[ModalityCodeEnum.SPECIAL_EXAMS]: refundModalityLabels.specialExam,
	[ModalityCodeEnum.HOSPITALIZATIONS]: modalityCards.hospitalization,
	[ModalityCodeEnum.EMERGENCY]: modalityCards.emergencyRoom,
	[ModalityCodeEnum.THERAPIES]: modalityCards.therapy,
};

export function RefundCardV2({data, onViewMoreClick}: RefundCardProps) {
	const {
		situationCode,
		estimatedPaymentDate,
		paymentDate,
		requestDate,
		totalReimbursementAmount,
		modalityCode,
		provider,
		protocol: protocolNumber,
	} = data;

	const theme = useTheme();
	const modalityDisplay =
		MODALITY_LABEL_BY_CODE[String(modalityCode) as ModalityCodeEnum] ?? detailValueNotInformed;
	const protocolDisplay = protocolNumber?.trim() ? protocolNumber : detailValueNotInformed;

	const dateRow = (() => {
		if (situationCode === RefundStatusEnum.CONCLUDED_8) {
			return {
				label: paymentDateTitle,
				icon: 'calendar-clock' as const,
				value: paymentDate,
			};
		}
		if (situationCode === RefundStatusEnum.APPROVED_7) {
			return {
				label: expectedPaymentDateTitle,
				icon: 'calendar-clock' as const,
				value: estimatedPaymentDate,
			};
		}
		return {
			label: requestedAt,
			icon: 'calendar-focused-filled' as const,
			value: requestDate,
		};
	})();
	const dateDisplay = dateRow.value?.trim()
		? formatRefundDate(dateRow.value)
		: detailValueNotInformed;

	const baseCardStyle = cardStyleByStatus(theme, situationCode);
	const footerChip =
		situationCode === RefundStatusEnum.IN_ANALYSIS_6
			? {
					chipText: refundStatusLabels.analysisShort,
					chipVariant: 'primary' as const,
				}
			: baseCardStyle.footer;

	const handleViewDetails = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onViewMoreClick(data);
	};

	const {isEnabled: isRN623Enabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_623_REEMBOLSO);

	const providerDisplayName = provider?.trim()
		? textHandle(provider).capitalize()
		: providerNamePlaceholder;

	const amountFormatted = formatRefundAmount(situationCode, totalReimbursementAmount);

	return (
		<StyledCard.Container sx={{height: '100%', justifyContent: 'flex-start', p: 0, gap: 0}}>
			<Box sx={{px: 2, pt: 2, pb: 2}}>
				<Stack gap={0.75} width="100%">
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={1}
						width="100%">
						<Typography
							component="span"
							sx={{
								fontSize: '12px',
								fontWeight: 600,
								lineHeight: 1.3,
								color: 'text.primary',
								flexShrink: 0,
							}}>
							{providerLabel}
						</Typography>
						<Box
							sx={{
								flexShrink: 1,
								minWidth: 0,
								maxWidth: {xs: 'min(100%, 200px)', sm: 220},
								display: 'flex',
								justifyContent: 'flex-end',
							}}>
							<TagChip
								label={footerChip.chipText}
								color={footerChip.chipVariant}
								sx={{
									maxWidth: '100%',
									'& .MuiChip-label': {
										display: 'block',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									},
								}}
							/>
						</Box>
					</Stack>
					<Typography
						component="p"
						sx={{
							fontSize: '18px',
							fontWeight: 400,
							lineHeight: 1.4,
							color: 'text.primary',
							width: '100%',
							margin: 0,
							overflowWrap: 'break-word',
							wordBreak: 'break-word',
						}}>
						{providerDisplayName}
					</Typography>
				</Stack>
			</Box>
			<Divider
				sx={{
					borderColor: theme.palette.success.shades?.['30p'],
				}}
			/>
			<Box
				sx={{
					p: 2,
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					flex: 1,
					minHeight: 0,
				}}>
				<StyledCard.Content
					sx={{
						pt: 0,
						pb: 0,
						marginBottom: 'auto',
						flexDirection: 'column',
					}}
					list={[
						{
							primaryText: modalityLabel,
							iconName: 'text-box-outline',
							secondaryText: modalityDisplay,
							primaryTextColor: 'text.secondary',
							primaryTextFontSize: '0.875rem',
							secondaryTextFontSize: '1rem',
						},
						{
							primaryText: protocol,
							iconName: 'assignment-outline',
							secondaryText: protocolDisplay,
							primaryTextColor: 'text.secondary',
							primaryTextFontSize: '0.875rem',
							secondaryTextFontSize: '1rem',
						},
						{
							primaryText: dateRow.label,
							iconName: dateRow.icon,
							secondaryText: dateDisplay,
							primaryTextColor: 'text.secondary',
							primaryTextFontSize: '0.875rem',
							secondaryTextFontSize: '1rem',
						},
						{
							primaryText: amountToReceive,
							iconName: 'paid',
							secondaryText: (
								<Typography
									component="span"
									variant="body1"
									sx={{
										fontSize: '24px',
										fontWeight: 600,
										color: 'primary.main',
										lineHeight: 1.3,
									}}>
									{amountFormatted}
								</Typography>
							),
							primaryTextColor: 'text.secondary',
							primaryTextFontSize: '0.875rem',
						},
					]}
				/>
				{isRN623Enabled ? (
					<Button
						fullWidth
						variant="outlined"
						size="large"
						onClick={handleViewDetails}
						data-tag-id={RefundTaggingEnum.CLICK_VER_MAIS_CONSULTAR_REEMBOLSO}>
						{cardActionSeeSummary}
					</Button>
				) : null}
			</Box>
		</StyledCard.Container>
	);
}
